/**
 * Real CloudBase Authentication Service
 * 真实的腾讯云 CloudBase 认证服务实现
 */

import { auth } from "./cloudbaseApp";
import type {
  AuthResult,
  CloudBaseUser,
  PhoneLoginCredentials,
  EmailCodeLoginCredentials,
  PasswordLoginCredentials,
  PhoneRegisterData,
  EmailRegisterData,
  SendCodeResult,
} from "./types";
import type { ICloudBaseAuthService } from "./authService";

/**
 * 格式化手机号（支持国际号码）
 *
 * 支持格式:
 * - 完整国际号码: +86 13800000000, +1 2025551234
 * - 缺少空格的国际号码: +8613800000000 → +86 13800000000
 * - 中国手机号(无国家代码): 13800000000 → +86 13800000000
 */
const formatPhoneNumber = (phone: string): string => {
  // 1. 如果已经是完整国际格式 (+86 13800000000),直接返回
  if (/^\+\d{1,3}\s\d+$/.test(phone)) {
    return phone;
  }

  // 2. 如果有国家代码但缺少空格 (+8613800000000),添加空格
  // 匹配: +[1-3位数字][后续数字]
  if (/^\+\d{1,3}\d+$/.test(phone)) {
    return phone.replace(/^(\+\d{1,3})(\d)/, "$1 $2");
  }

  // 3. 默认处理中国手机号 (13800000000 → +86 13800000000)
  // 仅对符合中国手机号格式的号码添加 +86
  if (/^1[3-9]\d{9}$/.test(phone)) {
    return `+86 ${phone}`;
  }

  // 4. 其他情况(可能是邮箱或格式错误),直接返回让后续验证处理
  return phone;
};

/**
 * 将 CloudBase 用户对象映射到我们的 CloudBaseUser 类型
 */
const mapCloudBaseUser = (user: any): CloudBaseUser => {
  // CloudBase可能返回不同的UID字段名：优先级 uuid > _id > user_id > uid
  const uid = user.uuid || user._id || user.user_id || user.uid;

  console.log("[CloudBase] Mapping user object:", {
    hasUid: !!user.uid,
    hasUserId: !!user.user_id,
    hasUuid: !!user.uuid,
    has_Id: !!user._id,
    finalUid: uid,
    extractedFrom: user.uuid
      ? "uuid"
      : user._id
        ? "_id"
        : user.user_id
          ? "user_id"
          : user.uid
            ? "uid"
            : "NONE",
    allKeys: Object.keys(user),
  });

  // 关键修复: 如果所有 UID 字段都不存在，抛出错误
  if (!uid) {
    console.error("[CloudBase] 无法提取用户 UID，完整用户对象:", user);
    throw new Error(
      "CloudBase 用户对象缺少 UID 字段（uuid/_id/user_id/uid），请联系技术支持",
    );
  }

  console.log("[CloudBase] 成功映射用户 UID:", {
    uid,
    extractedFrom: user.uuid
      ? "uuid"
      : user._id
        ? "_id"
        : user.user_id
          ? "user_id"
          : "uid",
    email: user.email,
    phone: user.phone_number || user.phone,
    nickName: user.nick_name,
  });

  return {
    uid,
    name: user.name, // CloudBase sys_user.name (e.g., "jt.wei")
    username: user.username || user.name,
    phone: user.phone_number || user.phone,
    email: user.email,
    nickName: user.nick_name || user.display_name || user.name,
    gender: user.gender,
    customUserId: user.custom_user_id,
    // 注册时必须设置密码,因此所有用户都有密码
    // hasPassword 字段已废弃,始终为 true
    hasPassword: true,
    // 业务扩展字段
    subscriptionTier: "free", // 默认免费版，可以后续从自定义属性读取
    createdAt: user.created_at || new Date().toISOString(),
  };
};

/**
 * 获取认证令牌
 * CloudBase SDK v2: 使用 auth.getAccessToken() 方法获取 token
 */
const getAuthTokens = async () => {
  try {
    // 1. 检查登录状态
    const loginState: any = await auth.getLoginState();
    console.log("[CloudBase] getLoginState 返回:", {
      hasLoginState: !!loginState,
      hasUser: !!loginState?.user,
      loginStateKeys: loginState ? Object.keys(loginState) : [],
      userUid: loginState?.user?.uid,
    });

    // CloudBase v2: loginState.user 存在表示已登录
    if (!loginState || !loginState.user) {
      throw new Error("用户未登录（loginState.user 不存在）");
    }

    // 2. 使用 auth.getAccessToken() 方法获取 token
    console.log("[CloudBase] 调用 auth.getAccessToken()...");
    const tokenResult: any = await auth.getAccessToken();

    console.log("[CloudBase] getAccessToken 返回:", {
      hasTokenResult: !!tokenResult,
      hasAccessToken: !!tokenResult?.accessToken,
      tokenLength: tokenResult?.accessToken?.length,
      env: tokenResult?.env,
    });

    if (!tokenResult || !tokenResult.accessToken) {
      throw new Error("accessToken 缺失");
    }

    return {
      token: tokenResult.accessToken,
      refreshToken: "", // CloudBase v2 可能不返回 refreshToken
    };
  } catch (error) {
    console.error("[CloudBase] getAuthTokens 失败:", error);
    throw error;
  }
};

/**
 * Real CloudBase Auth Service Implementation
 */
export const realCloudBaseAuth: ICloudBaseAuthService = {
  /**
   * 手机验证码登录
   * 使用 signIn() + verification_token 方式登录已存在用户
   * 如果用户不存在,应该引导到注册页面
   */
  async loginWithPhoneCode(
    credentials: PhoneLoginCredentials,
  ): Promise<AuthResult> {
    try {
      const { phone, code, verificationId } = credentials;
      const formattedPhone = formatPhoneNumber(phone);

      console.log("[CloudBase] 开始短信验证码登录", {
        phone,
        formattedPhone,
        code,
        verificationId,
        timestamp: new Date().toISOString(),
      });

      // 1. 先验证验证码并获取 verification_token
      let verificationToken: string;
      try {
        const verificationTokenRes: any = await auth.verify({
          verification_id: verificationId,
          verification_code: code,
        });
        verificationToken = verificationTokenRes?.verification_token;
        if (!verificationToken) {
          throw new Error("未获取到短信登录所需的 verification_token");
        }
        console.log("[CloudBase] 验证码验证成功,获得 verification_token");
      } catch (verifyError: any) {
        console.error("[CloudBase] 验证码验证失败:", verifyError);
        throw new Error("验证码错误或已过期");
      }

      // 2. 使用 signIn() + verification_token 登录(仅支持已存在用户)
      const signInResult = await auth.signIn({
        username: formattedPhone,
        verification_token: verificationToken,
      });

      console.log("[CloudBase] signIn 调用成功 (验证码登录)", {
        hasResult: !!signInResult,
        resultKeys: signInResult ? Object.keys(signInResult) : [],
      });

      // 获取当前用户信息和令牌
      const currentUser: any = auth.currentUser;
      console.log("[CloudBase] 当前用户信息", {
        hasUser: !!currentUser,
        userKeys: currentUser ? Object.keys(currentUser) : [],
        uid: currentUser?.uid,
        phone: currentUser?.phone_number,
      });

      if (!currentUser) {
        throw new Error("登录失败：未获取到用户信息");
      }

      const cloudbaseTokens = await getAuthTokens();
      console.log("[CloudBase] 获取到 CloudBase 令牌", {
        hasToken: !!cloudbaseTokens.token,
      });

      const user = mapCloudBaseUser(currentUser);

      console.log("[CloudBase] 短信登录成功", {
        uid: user.uid,
        phone: user.phone,
      });

      return {
        uid: user.uid,
        loginType: "PHONE",
        token: cloudbaseTokens.token,
        refreshToken: cloudbaseTokens.refreshToken,
        user,
      };
    } catch (error: any) {
      console.error("[CloudBase] 短信验证码登录失败 - 详细错误:", {
        errorMessage: error.message,
        errorCode: error.code,
        errorName: error.name,
        errorStack: error.stack,
        fullError: error,
      });

      // 如果是用户不存在错误,提供更友好的提示
      if (
        error.code === "user_not_found" ||
        error.message?.includes("不存在")
      ) {
        throw new Error("该手机号未注册,请先注册");
      }

      throw new Error(error.message || "手机验证码登录失败");
    }
  },

  /**
   * 邮箱验证码登录
   * 使用 signIn() + verification_token 方式登录已存在用户
   * 如果用户不存在,应该引导到注册页面
   */
  async loginWithEmailCode(
    credentials: EmailCodeLoginCredentials,
  ): Promise<AuthResult> {
    try {
      const { email, code, verificationId } = credentials;

      console.log("[CloudBase] 开始邮箱验证码登录", {
        email,
        code,
        verificationId,
        timestamp: new Date().toISOString(),
      });

      // 1. 先验证验证码并获取 verification_token
      let verificationToken: string;
      try {
        const verificationTokenRes: any = await auth.verify({
          verification_id: verificationId,
          verification_code: code,
        });
        verificationToken = verificationTokenRes?.verification_token;
        if (!verificationToken) {
          throw new Error("未获取到邮箱登录所需的 verification_token");
        }
        console.log("[CloudBase] 验证码验证成功,获得 verification_token");
      } catch (verifyError: any) {
        console.error("[CloudBase] 验证码验证失败:", verifyError);
        throw new Error("验证码错误或已过期");
      }

      // 2. 使用 signIn() + verification_token 登录(仅支持已存在用户)
      const signInResult = await auth.signIn({
        username: email,
        verification_token: verificationToken,
      });

      console.log("[CloudBase] signIn 调用成功 (邮箱验证码登录)", {
        hasResult: !!signInResult,
        resultKeys: signInResult ? Object.keys(signInResult) : [],
      });

      // 获取当前用户信息和令牌
      const currentUser: any = auth.currentUser;
      console.log("[CloudBase] 当前用户信息", {
        hasUser: !!currentUser,
        userKeys: currentUser ? Object.keys(currentUser) : [],
        uid: currentUser?.uid,
        email: currentUser?.email,
      });

      if (!currentUser) {
        throw new Error("登录失败：未获取到用户信息");
      }

      const cloudbaseTokens = await getAuthTokens();
      console.log("[CloudBase] 获取到 CloudBase 令牌", {
        hasToken: !!cloudbaseTokens.token,
      });

      const user = mapCloudBaseUser(currentUser);

      console.log("[CloudBase] 邮箱验证码登录成功", {
        uid: user.uid,
        email: user.email,
      });

      return {
        uid: user.uid,
        loginType: "EMAIL",
        token: cloudbaseTokens.token,
        refreshToken: cloudbaseTokens.refreshToken,
        user,
      };
    } catch (error: any) {
      console.error("[CloudBase] 邮箱验证码登录失败 - 详细错误:", {
        errorMessage: error.message,
        errorCode: error.code,
        errorName: error.name,
        errorStack: error.stack,
        fullError: error,
      });

      // 如果是用户不存在错误,提供更友好的提示
      if (
        error.code === "user_not_found" ||
        error.message?.includes("不存在")
      ) {
        throw new Error("该邮箱未注册,请先注册");
      }

      throw new Error(error.message || "邮箱验证码登录失败");
    }
  },

  /**
   * 密码登录（支持手机号或邮箱）
   */
  async loginWithPassword(
    credentials: PasswordLoginCredentials,
  ): Promise<AuthResult> {
    try {
      const { account, password } = credentials;

      // 关键修复: 如果是手机号,需要格式化为 "+86 手机号" 格式
      // 因为注册时使用的是 formatPhoneNumber(),必须保持一致
      const isPhone = !account.includes("@");
      const formattedAccount = isPhone ? formatPhoneNumber(account) : account;

      console.log("[CloudBase] 开始密码登录", {
        account,
        formattedAccount,
        accountType: isPhone ? "PHONE" : "EMAIL",
        timestamp: new Date().toISOString(),
      });

      // 使用 signIn 方法进行密码登录
      const signInResult = await auth.signIn({
        username: formattedAccount,
        password,
      });

      console.log("[CloudBase] signIn 调用成功", {
        hasResult: !!signInResult,
        resultKeys: signInResult ? Object.keys(signInResult) : [],
      });

      // 获取当前用户信息和令牌
      const currentUser: any = auth.currentUser;
      console.log("[CloudBase] 当前用户信息", {
        hasUser: !!currentUser,
        userKeys: currentUser ? Object.keys(currentUser) : [],
        uid: currentUser?.uid,
        email: currentUser?.email,
        phone: currentUser?.phone_number,
      });

      if (!currentUser) {
        throw new Error("登录失败：未获取到用户信息");
      }

      const cloudbaseTokens = await getAuthTokens();
      console.log("[CloudBase] 获取到 CloudBase 令牌", {
        hasToken: !!cloudbaseTokens.token,
        tokenLength: cloudbaseTokens.token?.length,
      });

      const user = mapCloudBaseUser(currentUser);

      // 判断登录类型
      const loginType = account.includes("@") ? "EMAIL" : "PHONE";

      console.log("[CloudBase] 密码登录成功", {
        uid: user.uid,
        loginType,
        email: user.email,
        phone: user.phone,
      });

      return {
        uid: user.uid,
        loginType,
        token: cloudbaseTokens.token,
        refreshToken: cloudbaseTokens.refreshToken,
        user,
      };
    } catch (error: any) {
      console.error("[CloudBase] 密码登录失败 - 详细错误:", {
        errorMessage: error.message,
        errorCode: error.code,
        errorName: error.name,
        errorStack: error.stack,
        fullError: error,
      });
      throw new Error(error.message || "账号或密码错误");
    }
  },

  /**
   * 手机号注册
   */
  async registerByPhone(data: PhoneRegisterData): Promise<AuthResult> {
    try {
      const { phone, code, password, verificationId } = data;
      const formattedPhone = formatPhoneNumber(phone);

      console.log("[CloudBase] ========== 开始手机号注册 ==========");
      console.log("[CloudBase] 注册参数:", {
        phone,
        formattedPhone,
        code: code ? "(已提供)" : "(缺失)",
        verificationId: verificationId ? "(已提供)" : "(缺失)",
        password: password ? "(已提供)" : "(缺失)",
        timestamp: new Date().toISOString(),
      });

      // 1. 验证验证码
      console.log("[CloudBase] 步骤1: 验证验证码...");
      const verificationTokenRes = await auth.verify({
        verification_id: verificationId,
        verification_code: code,
      });

      console.log("[CloudBase] ✓ 验证码验证成功", {
        hasToken: !!verificationTokenRes.verification_token,
      });

      // 2. 注册新用户
      console.log("[CloudBase] 步骤2: 调用 signUp() 注册新用户...");
      const signUpResult = await auth.signUp({
        phone_number: formattedPhone,
        verification_code: code,
        verification_token: verificationTokenRes.verification_token,
        password,
      });

      console.log("[CloudBase] ✓ signUp() 调用成功", {
        hasResult: !!signUpResult,
        resultKeys: signUpResult ? Object.keys(signUpResult) : [],
      });

      // 3. 检查登录态
      console.log("[CloudBase] 步骤3: 检查注册后的登录态...");
      let loginState = await auth.getLoginState();
      console.log("[CloudBase] 注册后登录态:", {
        hasLoginState: !!loginState,
        hasUser: !!loginState?.user,
        userUid: loginState?.user?.uid,
      });

      // 4. 如果未自动登录，手动登录
      // CloudBase v2: 检查 loginState.user 是否存在
      if (!loginState || !loginState.user) {
        console.log("[CloudBase] ⚠️ 注册后未自动登录，执行手动登录...");
        try {
          await auth.signIn({
            username: formattedPhone,
            password,
          });
          console.log("[CloudBase] ✓ 手动登录成功");

          loginState = await auth.getLoginState();
          console.log("[CloudBase] 手动登录后的登录态:", {
            hasUser: !!loginState?.user,
            userUid: loginState?.user?.uid,
          });
        } catch (signInError) {
          console.error("[CloudBase] ❌ 手动登录失败:", signInError);
          throw new Error("注册成功但自动登录失败，请手动登录");
        }
      }

      // 5. 获取当前用户信息
      console.log("[CloudBase] 步骤5: 获取用户信息...");
      const currentUser: any = auth.currentUser;
      console.log("[CloudBase] currentUser:", {
        hasUser: !!currentUser,
        userKeys: currentUser ? Object.keys(currentUser) : [],
      });

      if (!currentUser) {
        throw new Error("注册成功但无法获取用户信息，请尝试重新登录");
      }

      // 6. 获取认证令牌
      console.log("[CloudBase] 步骤6: 获取 CloudBase 令牌...");
      const cloudbaseTokens = await getAuthTokens();
      console.log("[CloudBase] ✓ CloudBase 令牌获取成功");

      // 7. 映射用户对象
      console.log("[CloudBase] 步骤7: 映射用户对象...");
      const user = mapCloudBaseUser(currentUser);

      console.log("[CloudBase] ========== 手机号注册成功 ==========");
      console.log("[CloudBase] 最终结果:", {
        uid: user.uid,
        phone: user.phone,
        hasToken: !!cloudbaseTokens.token,
      });

      return {
        uid: user.uid,
        loginType: "PHONE",
        token: cloudbaseTokens.token,
        refreshToken: cloudbaseTokens.refreshToken,
        user,
      };
    } catch (error: any) {
      console.error("[CloudBase] ========== 手机号注册失败 ==========");
      console.error("[CloudBase] 错误详情:", {
        errorMessage: error.message,
        errorCode: error.code,
        errorName: error.name,
        errorStack: error.stack,
      });

      // 处理用户已存在的情况
      if (
        error.code === "USER_EXISTS" ||
        error.code === "user_exists" ||
        error.message?.includes("已存在") ||
        error.message?.includes("exists")
      ) {
        throw new Error("该手机号已注册，请前往登录页面");
      }

      throw new Error(error.message || "手机号注册失败，请稍后重试");
    }
  },

  /**
   * 邮箱注册
   */
  async registerByEmail(data: EmailRegisterData): Promise<AuthResult> {
    try {
      const { email, code, password, verificationId } = data;

      console.log("[CloudBase] ========== 开始邮箱注册 ==========");
      console.log("[CloudBase] 注册参数:", {
        email,
        code: code ? "(已提供)" : "(缺失)",
        verificationId: verificationId ? "(已提供)" : "(缺失)",
        password: password ? "(已提供)" : "(缺失)",
        timestamp: new Date().toISOString(),
      });

      // 1. 验证验证码
      console.log("[CloudBase] 步骤1: 验证验证码...");
      const verificationTokenRes = await auth.verify({
        verification_id: verificationId,
        verification_code: code,
      });

      console.log("[CloudBase] ✓ 验证码验证成功", {
        hasToken: !!verificationTokenRes.verification_token,
        tokenLength: verificationTokenRes.verification_token?.length,
      });

      // 2. 注册新用户
      console.log("[CloudBase] 步骤2: 调用 signUp() 注册新用户...");
      const signUpResult = await auth.signUp({
        email,
        verification_code: code,
        verification_token: verificationTokenRes.verification_token,
        password,
      });

      console.log("[CloudBase] ✓ signUp() 调用成功", {
        hasResult: !!signUpResult,
        resultKeys: signUpResult ? Object.keys(signUpResult) : [],
      });

      // 3. 检查登录态 - 关键步骤
      console.log("[CloudBase] 步骤3: 检查注册后的登录态...");
      let loginState = await auth.getLoginState();
      console.log("[CloudBase] 注册后登录态:", {
        hasLoginState: !!loginState,
        hasUser: !!loginState?.user,
        userUid: loginState?.user?.uid,
      });

      // 4. 如果未自动登录，手动登录
      // CloudBase v2: 检查 loginState.user 是否存在
      if (!loginState || !loginState.user) {
        console.log("[CloudBase] ⚠️ 注册后未自动登录，执行手动登录...");
        try {
          await auth.signIn({
            username: email,
            password,
          });
          console.log("[CloudBase] ✓ 手动登录成功");

          // 重新获取登录态
          loginState = await auth.getLoginState();
          console.log("[CloudBase] 手动登录后的登录态:", {
            hasUser: !!loginState?.user,
            userUid: loginState?.user?.uid,
          });
        } catch (signInError) {
          console.error("[CloudBase] ❌ 手动登录失败:", signInError);
          throw new Error("注册成功但自动登录失败，请手动登录");
        }
      }

      // 5. 获取当前用户信息
      console.log("[CloudBase] 步骤5: 获取用户信息...");
      const currentUser: any = auth.currentUser;
      console.log("[CloudBase] currentUser:", {
        hasUser: !!currentUser,
        userKeys: currentUser ? Object.keys(currentUser) : [],
      });

      if (!currentUser) {
        throw new Error("注册成功但无法获取用户信息，请尝试重新登录");
      }

      // 6. 获取认证令牌
      console.log("[CloudBase] 步骤6: 获取 CloudBase 令牌...");
      const cloudbaseTokens = await getAuthTokens();
      console.log("[CloudBase] ✓ CloudBase 令牌获取成功");

      // 7. 映射用户对象
      console.log("[CloudBase] 步骤7: 映射用户对象...");
      const user = mapCloudBaseUser(currentUser);

      console.log("[CloudBase] ========== 邮箱注册成功 ==========");
      console.log("[CloudBase] 最终结果:", {
        uid: user.uid,
        email: user.email,
        nickName: user.nickName,
        hasToken: !!cloudbaseTokens.token,
      });

      return {
        uid: user.uid,
        loginType: "EMAIL",
        token: cloudbaseTokens.token,
        refreshToken: cloudbaseTokens.refreshToken,
        user,
      };
    } catch (error: any) {
      console.error("[CloudBase] ========== 邮箱注册失败 ==========");
      console.error("[CloudBase] 错误详情:", {
        errorMessage: error.message,
        errorCode: error.code,
        errorName: error.name,
        errorStack: error.stack,
      });

      // 处理用户已存在的情况
      if (
        error.code === "USER_EXISTS" ||
        error.code === "user_exists" ||
        error.message?.includes("已存在") ||
        error.message?.includes("exists")
      ) {
        throw new Error("该邮箱已注册，请前往登录页面");
      }

      throw new Error(error.message || "邮箱注册失败，请稍后重试");
    }
  },

  /**
   * 发送短信验证码
   */
  async sendSmsCode(phone: string): Promise<SendCodeResult> {
    try {
      const formattedPhone = formatPhoneNumber(phone);

      console.log("[CloudBase] 发送短信验证码", {
        phone,
        formattedPhone,
        timestamp: new Date().toISOString(),
      });

      const verification: any = await auth.getVerification({
        phone_number: formattedPhone,
      });
      const verificationId = verification?.verification_id;
      if (!verificationId) {
        throw new Error("未获取到短信验证码 ID");
      }

      console.log("[CloudBase] 短信验证码发送成功", {
        verificationId: verification.verification_id,
      });

      return {
        success: true,
        verificationId,
        message: "验证码已发送",
      };
    } catch (error: any) {
      console.error("[CloudBase] 发送短信验证码失败 - 详细错误:", {
        errorMessage: error.message,
        errorCode: error.code,
        errorName: error.name,
        fullError: error,
      });
      throw new Error(error.message || "发送验证码失败");
    }
  },

  /**
   * 发送邮箱验证码
   */
  async sendEmailCode(email: string): Promise<SendCodeResult> {
    try {
      console.log("[CloudBase] 发送邮箱验证码", {
        email,
        timestamp: new Date().toISOString(),
      });

      const verification: any = await auth.getVerification({
        email,
      });
      const verificationId = verification?.verification_id;
      if (!verificationId) {
        throw new Error("未获取到邮箱验证码 ID");
      }

      console.log("[CloudBase] 邮箱验证码发送成功", {
        verificationId: verification.verification_id,
      });

      return {
        success: true,
        verificationId,
        message: "验证码已发送至您的邮箱",
      };
    } catch (error: any) {
      console.error("[CloudBase] 发送邮箱验证码失败 - 详细错误:", {
        errorMessage: error.message,
        errorCode: error.code,
        errorName: error.name,
        fullError: error,
      });
      throw new Error(error.message || "发送验证码失败");
    }
  },

  /**
   * 获取当前用户
   */
  async getCurrentUser(_token: string): Promise<CloudBaseUser | null> {
    try {
      // 检查登录状态
      const loginState: any = await auth.getLoginState();
      if (!loginState || !loginState.user) {
        return null;
      }

      const currentUser: any = auth.currentUser;
      if (!currentUser) {
        return null;
      }

      return mapCloudBaseUser(currentUser);
    } catch (error: any) {
      console.error("[CloudBase] Get current user error:", error);
      return null;
    }
  },

  /**
   * 登出
   */
  async logout(): Promise<void> {
    try {
      await auth.signOut();
    } catch (error: any) {
      console.error("[CloudBase] Logout error:", error);
      throw new Error(error.message || "登出失败");
    }
  },
};
